import { apiFetch } from './api.js';

/**
 * App 3 endpoints (spec §11).
 *
 * In its own module for the same reason App 2's are: the audit lifecycle is long
 * and has several distinct actors, and keeping its calls together makes it
 * obvious which screen belongs to which phase.
 */

export type AuditPhase = 'pre_audit' | 'execution' | 'post_audit';
export type AuditStatus =
  | 'scheduled'
  | 'pending_approval'
  | 'approved'
  | 'sent_back'
  | 'in_progress'
  | 'reporting'
  | 'closed';
export type RecordStatus = 'draft' | 'submitted' | 'sent_back' | 'approved';
export type FindingClassification = 'conformity' | 'observation' | 'minor_nc' | 'major_nc';
export type ChecklistResponseValue =
  | 'conforms'
  | 'does_not_conform'
  | 'not_applicable'
  | 'unanswered';
export type CarStatus =
  | 'issued'
  | 'in_progress'
  | 'submitted'
  | 'verifying'
  | 'closed_effective'
  | 'closed_not_effective'
  | 'follow_up_scheduled';

export interface AuditSchedule {
  id: string;
  year: number;
  title: string;
  status: RecordStatus;
  submittedBy: string | null;
  submittedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  sentBackRemarks: string | null;
  memoFileId: string | null;
  memoDistributedAt: string | null;
}

export interface AuditPlan {
  id: string;
  scheduleId: string | null;
  title: string;
  scope: string | null;
  criteria: string | null;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  location: string | null;
  feasibilityNotes: string | null;
  status: RecordStatus;
  sharedAt: string | null;
}

export interface Audit {
  id: string;
  planId: string | null;
  projectId: string | null;
  referenceNo: string;
  title: string;
  phase: AuditPhase;
  status: AuditStatus;
  leadAuditorId: string | null;
  teamUserIds: string[];
  actualStartDate: string | null;
  actualEndDate: string | null;
  openingMeetingAt: string | null;
  openingMeetingMinutes: string | null;
  closingMeetingAt: string | null;
  closingMeetingMinutes: string | null;
  closedAt: string | null;
}

export interface ChecklistTemplate {
  id: string;
  name: string;
  description: string | null;
  auditTypeKey: string | null;
}

export interface ChecklistItem {
  id: string;
  templateId: string;
  clause: string | null;
  sequence: number;
  question: string;
  guidance: string | null;
}

export interface ChecklistResponse {
  id: string;
  templateItemId: string;
  /** The item's question and clause, so an answer reads on its own. */
  question?: string | null;
  clause?: string | null;
  response: ChecklistResponseValue;
  notes: string | null;
  findingId: string | null;
  answeredAt: string | null;
}

export interface ChecklistProgress {
  total: number;
  answered: number;
  unanswered: number;
  nonConforming: number;
  nonConformingWithoutFinding: number;
}

export interface AuditEvidence {
  id: string;
  checklistResponseId: string | null;
  evidenceType: 'document' | 'photo' | 'interview' | 'observation';
  description: string | null;
  fileId: string | null;
  gpsLatitude: string | null;
  gpsLongitude: string | null;
  gpsCaptureStatus: 'captured' | 'denied' | 'unavailable' | 'timed_out';
  capturedAt: string | null;
}

export interface AuditFinding {
  id: string;
  auditId: string;
  referenceNo: string;
  classification: FindingClassification;
  clause: string | null;
  description: string;
  evidenceSummary: string | null;
  riskLevel: string | null;
  closedAt: string | null;
}

export interface CorrectiveAction {
  id: string;
  findingId: string;
  responsibleBusinessUnitId: string;
  ownerUserId: string | null;
  referenceNo: string;
  rootCause: string;
  correction: string;
  correctiveAction: string;
  targetCompletionDate: string | null;
  status: CarStatus;
  submittedAt: string | null;
  verifiedAt: string | null;
  verificationNotes: string | null;
  followUpAuditId: string | null;
  /** The ineffective CAR this one follows up (same department). */
  supersedesCarId?: string | null;
  extensionRequestedUntil: string | null;
  extensionReason: string | null;
  extensionApprovedAt: string | null;
}

// --- Pre-Audit: the annual schedule ----------------------------------------

export const fetchSchedules = () => apiFetch<{ schedules: AuditSchedule[] }>('/audit/schedules');

export const createSchedule = (body: { year: number; title: string }) =>
  apiFetch<{ schedule: AuditSchedule }>('/audit/schedules', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const submitSchedule = (id: string) =>
  apiFetch<{ schedule: AuditSchedule }>(`/audit/schedules/${id}/submit`, { method: 'POST' });

export const approveSchedule = (id: string) =>
  apiFetch<{ schedule: AuditSchedule }>(`/audit/schedules/${id}/approve`, { method: 'POST' });

export const sendBackSchedule = (id: string, remarks: string) =>
  apiFetch<{ schedule: AuditSchedule }>(`/audit/schedules/${id}/send-back`, {
    method: 'POST',
    body: JSON.stringify({ remarks }),
  });

// --- Pre-Audit: the plan ----------------------------------------------------

export const fetchPlans = (scheduleId?: string) =>
  apiFetch<{ plans: AuditPlan[] }>(
    `/audit/plans${scheduleId ? `?scheduleId=${scheduleId}` : ''}`,
  );

export const createPlan = (body: Record<string, unknown>) =>
  apiFetch<{ plan: AuditPlan }>('/audit/plans', { method: 'POST', body: JSON.stringify(body) });

export const updatePlan = (id: string, body: Record<string, unknown>) =>
  apiFetch<{ plan: AuditPlan }>(`/audit/plans/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const sharePlan = (id: string) =>
  apiFetch<{ plan: AuditPlan }>(`/audit/plans/${id}/share`, { method: 'POST' });

// --- The engagement ---------------------------------------------------------

export interface AuditFilters {
  projectId?: string;
  phase?: AuditPhase;
  status?: AuditStatus;
  search?: string;
}

export const fetchAudits = (filters: AuditFilters = {}) => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) query.set(key, value);
  }
  const suffix = query.toString();
  return apiFetch<{ audits: Audit[] }>(`/audit/audits${suffix ? `?${suffix}` : ''}`);
};

export const fetchAudit = (id: string) => apiFetch<{ audit: Audit }>(`/audit/audits/${id}`);

export const createAudit = (body: Record<string, unknown>) =>
  apiFetch<{ audit: Audit }>('/audit/audits', { method: 'POST', body: JSON.stringify(body) });

export const recordMinutes = (
  id: string,
  body: { meeting: 'opening' | 'closing'; heldAt: string; minutes: string },
) =>
  apiFetch<{ audit: Audit }>(`/audit/audits/${id}/minutes`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// --- Execution --------------------------------------------------------------

export const fetchChecklistTemplates = () =>
  apiFetch<{ templates: ChecklistTemplate[] }>('/audit/checklist-templates');

export const fetchChecklistItems = (templateId: string) =>
  apiFetch<{ items: ChecklistItem[] }>(`/audit/checklist-templates/${templateId}/items`);

export const fetchChecklist = (auditId: string, templateId?: string) =>
  apiFetch<{ responses: ChecklistResponse[]; progress: ChecklistProgress }>(
    `/audit/audits/${auditId}/checklist${templateId ? `?templateId=${templateId}` : ''}`,
  );

export const answerChecklistItem = (
  auditId: string,
  body: { templateItemId: string; response: ChecklistResponseValue; notes?: string },
) =>
  apiFetch<{ response: ChecklistResponse }>(`/audit/audits/${auditId}/checklist`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const fetchEvidence = (auditId: string) =>
  apiFetch<{ evidence: AuditEvidence[] }>(`/audit/audits/${auditId}/evidence`);

export const addEvidence = (auditId: string, body: Record<string, unknown>) =>
  apiFetch<{ evidence: AuditEvidence }>(`/audit/audits/${auditId}/evidence`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const fetchFindings = (auditId: string) =>
  apiFetch<{ findings: AuditFinding[] }>(`/audit/audits/${auditId}/findings`);

export const createFinding = (auditId: string, body: Record<string, unknown>) =>
  apiFetch<{ finding: AuditFinding }>(`/audit/audits/${auditId}/findings`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateFinding = (id: string, body: Record<string, unknown>) =>
  apiFetch<{ finding: AuditFinding }>(`/audit/findings/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const fetchClosureBlockers = (auditId: string, templateId?: string) =>
  apiFetch<{ blockers: string[] }>(
    `/audit/audits/${auditId}/closure${templateId ? `?templateId=${templateId}` : ''}`,
  );

export const closeAudit = (auditId: string, templateId?: string) =>
  apiFetch<{ audit: Audit }>(`/audit/audits/${auditId}/close`, {
    method: 'POST',
    body: JSON.stringify({ templateId: templateId ?? null }),
  });


// --- Generated documents (spec §11) -----------------------------------------

export interface AuditReportDocument {
  referenceNo: string;
  title: string;
  generatedAt: string;
  isProvisional: boolean;
  provisionalReason: string | null;
  plan: {
    title: string;
    scope: string | null;
    criteria: string | null;
    location: string | null;
    plannedStartDate: string | null;
    plannedEndDate: string | null;
    sharedAt: string | null;
  } | null;
  audit: {
    referenceNo: string;
    title: string;
    phase: AuditPhase;
    status: string;
    openingMeetingAt: string | null;
    openingMeetingMinutes: string | null;
    closingMeetingAt: string | null;
    closingMeetingMinutes: string | null;
    closedAt: string | null;
    leadAuditorName: string | null;
    teamNames: string[];
  };
  sections: { key: string; heading: string; absence: string | null }[];
  checklist: ChecklistProgress;
  findings: (AuditFinding & {
    correctiveActions: {
      referenceNo: string;
      departmentName: string | null;
      ownerName: string | null;
      targetCompletionDate: string | null;
      status: string;
      verifiedAt: string | null;
      verificationNotes: string | null;
    }[];
  })[];
  evidence: {
    evidenceType: string;
    description: string | null;
    gpsCaptureStatus: string;
    capturedAt: string | null;
  }[];
  counts: {
    findings: number;
    findingsOpen: number;
    cars: number;
    carsOpen: number;
    byClassification: Record<string, number>;
  };
  caveats: string[];
}

export const fetchAuditReport = (auditId: string, templateId?: string) =>
  apiFetch<{
    report: AuditReportDocument;
    /** The document of record, once issued. */
    issued: { id: string; title: string; createdAt: string } | null;
  }>(
    `/audit/audits/${auditId}/report${templateId ? `?templateId=${templateId}` : ''}`,
  );

export interface ApprovalMemoDocument {
  subject: string;
  year: number;
  approvedAt: string | null;
  approverName: string | null;
  plannedAudits: { title: string; plannedStartDate: string | null; location: string | null }[];
  generatedAt: string;
  distributed: boolean;
  distributedAt: string | null;
  distributionNote: string;
}

export const fetchApprovalMemo = (scheduleId: string) =>
  apiFetch<{ memo: ApprovalMemoDocument; issued: { id: string; title: string; createdAt: string } | null }>(
    `/audit/schedules/${scheduleId}/memo`,
  );
// --- The Findings Log and the Audit Dashboard -------------------------------

export interface FindingLogRow extends AuditFinding {
  auditReferenceNo: string;
  auditTitle: string;
  projectId: string | null;
  createdAt: string;
}

export interface FindingsLogFilters {
  auditId?: string;
  projectId?: string;
  classification?: FindingClassification;
  state?: 'open' | 'closed';
  riskLevel?: string;
}

export const fetchFindingsLog = (filters: FindingsLogFilters = {}) => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) query.set(key, value);
  }
  const suffix = query.toString();
  return apiFetch<{ findings: FindingLogRow[] }>(`/audit/findings${suffix ? `?${suffix}` : ''}`);
};

export interface AuditDashboardSummary {
  audits: { total: number; open: number; closed: number };
  findings: number;
  findingsOpen: number;
  cars: number;
  carsOpen: number;
  byClassification: Record<string, number>;
}

export const fetchAuditDashboard = () =>
  apiFetch<{ summary: AuditDashboardSummary }>('/audit/dashboard');

// --- Post-Audit: corrective actions -----------------------------------------

export const fetchCorrectiveActions = (findingId: string) =>
  apiFetch<{ correctiveActions: CorrectiveAction[] }>(
    `/audit/findings/${findingId}/corrective-actions`,
  );

export const issueCorrectiveActions = (
  findingId: string,
  departments: { businessUnitId: string; ownerUserId?: string | null; targetCompletionDate?: string | null }[],
) =>
  apiFetch<{ correctiveActions: CorrectiveAction[] }>(
    `/audit/findings/${findingId}/corrective-actions`,
    { method: 'POST', body: JSON.stringify({ departments }) },
  );

export const updateCorrectiveAction = (id: string, body: Record<string, unknown>) =>
  apiFetch<{ correctiveAction: CorrectiveAction }>(`/audit/corrective-actions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const submitCorrectiveAction = (id: string) =>
  apiFetch<{ correctiveAction: CorrectiveAction }>(`/audit/corrective-actions/${id}/submit`, {
    method: 'POST',
  });

export const verifyCorrectiveAction = (
  id: string,
  body: { effective: boolean; notes: string; followUpAuditId?: string | null },
) =>
  apiFetch<{ correctiveAction: CorrectiveAction }>(`/audit/corrective-actions/${id}/verify`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const requestCarExtension = (id: string, body: { until: string; reason: string }) =>
  apiFetch<{ correctiveAction: CorrectiveAction }>(`/audit/corrective-actions/${id}/extension`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const approveCarExtension = (id: string) =>
  apiFetch<{ correctiveAction: CorrectiveAction }>(
    `/audit/corrective-actions/${id}/extension/approve`,
    { method: 'POST' },
  );

// --- Guest Auditors (SEC-8) -------------------------------------------------

export interface GuestInvitation {
  id: string;
  userId: string;
  displayName: string;
  email: string;
  expiresAt: string;
  revokedAt: string | null;
  createdAt: string;
}

export interface GuestAccount {
  id: string;
  displayName: string;
  email: string;
}

export const fetchAuditGuests = (auditId: string) =>
  apiFetch<{ guests: GuestInvitation[] }>(`/audit/audits/${auditId}/guests`);

export const fetchGuestAccounts = () => apiFetch<{ guests: GuestAccount[] }>('/audit/guest-auditors');

export const inviteGuest = (auditId: string, body: { userId: string; expiresAt: string }) =>
  apiFetch<{ guest: GuestInvitation }>(`/audit/audits/${auditId}/guests`, { method: 'POST', body: JSON.stringify(body) });

export const revokeGuest = (auditId: string, accessId: string) =>
  apiFetch<{ guest: GuestInvitation }>(`/audit/audits/${auditId}/guests/${accessId}`, { method: 'DELETE' });

// --- Pass 7 screens (FN-8, FN-10) -------------------------------------------

export interface AuditPerson {
  id: string;
  displayName: string;
  designation: string | null;
}

/** People an audit screen can name: lead auditor, team, CAR owner. */
export const fetchAuditPeople = () => apiFetch<{ people: AuditPerson[] }>('/audit/people');

/** Lead auditor and team — while the audit is still in pre-audit. */
export const updateAudit = (id: string, body: { leadAuditorId?: string | null; teamUserIds?: string[]; title?: string }) =>
  apiFetch<{ audit: Audit }>(`/audit/audits/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

/** A programme's year or title — while it is a draft or sent back. */
export const updateSchedule = (id: string, body: { year?: number; title?: string }) =>
  apiFetch<{ schedule: AuditSchedule }>(`/audit/schedules/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

export const createChecklistTemplate = (body: { name: string; description?: string | null; auditTypeKey?: string | null }) =>
  apiFetch<{ template: ChecklistTemplate }>('/audit/checklist-templates', { method: 'POST', body: JSON.stringify(body) });

export const addChecklistItem = (templateId: string, body: { clause?: string | null; sequence?: number; question: string; guidance?: string | null }) =>
  apiFetch<{ item: ChecklistItem }>(`/audit/checklist-templates/${templateId}/items`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
